import { supabase } from "~/integrations/supabase/client";
import { RESULTADOS_CLASES } from "~/types/contabilidad";

import type {
  BudgetVsActualAccount,
  BudgetVsActualClase,
  BudgetVsActualFilters,
  BudgetVsActualGroupedData,
  BudgetVsActualGrupo,
  BudgetVsActualLine,
  BudgetVsActualSubClase,
  BudgetVsActualSubClase2,
  BudgetVsActualValues,
} from "~/types/contabilidad";

const zeroValues = (): BudgetVsActualValues => ({
  budgetMes: 0,
  realMes: 0,
  budgetAcumulado: 0,
  realAcumulado: 0,
});

const sumValues = (items: BudgetVsActualValues[]): BudgetVsActualValues =>
  items.reduce(
    (acc, item) => ({
      budgetMes: acc.budgetMes + item.budgetMes,
      realMes: acc.realMes + item.realMes,
      budgetAcumulado: acc.budgetAcumulado + item.budgetAcumulado,
      realAcumulado: acc.realAcumulado + item.realAcumulado,
    }),
    zeroValues()
  );

const addValues = (
  a: BudgetVsActualValues,
  b: BudgetVsActualValues
): BudgetVsActualValues => ({
  budgetMes: a.budgetMes + b.budgetMes,
  realMes: a.realMes + b.realMes,
  budgetAcumulado: a.budgetAcumulado + b.budgetAcumulado,
  realAcumulado: a.realAcumulado + b.realAcumulado,
});

export const budgetVsActualService = {
  /**
   * Fetch budget vs actual data. Both series arrive in the signed report
   * convention (ingresos +, costos/gastos -), so desviacion = real - budget
   * is always favorable when positive.
   */
  async getBudgetVsActual(
    filters: BudgetVsActualFilters
  ): Promise<BudgetVsActualLine[]> {
    const { data, error } = await supabase.rpc("get_budget_vs_actual", {
      p_year: filters.year,
      p_month: filters.month,
      p_company_id: filters.companyId,
      p_template_id: filters.templateId,
      p_budget_id: filters.budgetId || null,
    });

    if (error) throw error;
    return (data || []) as BudgetVsActualLine[];
  },

  /**
   * Transform flat rows into the income-statement hierarchy
   * (Clase -> SubClase -> SubClase2 -> Grupo -> Account). Rows with
   * has_mapping = false are split into `unmapped` for the mismatch alert.
   */
  groupBudgetVsActualData(
    lines: BudgetVsActualLine[]
  ): BudgetVsActualGroupedData {
    const mapped = lines.filter((line) => line.has_mapping);
    const unmapped = lines.filter((line) => !line.has_mapping);

    const claseMap = new Map<string, BudgetVsActualLine[]>();

    mapped.forEach((line) => {
      const key = line.clase || "Sin Clase";
      const existing = claseMap.get(key) || [];
      existing.push(line);
      claseMap.set(key, existing);
    });

    const clases: BudgetVsActualClase[] = [];

    claseMap.forEach((claseLines, claseName) => {
      const subClaseMap = new Map<string, BudgetVsActualLine[]>();

      claseLines.forEach((line) => {
        const key = line.sub_clase || "Sin SubClase";
        const existing = subClaseMap.get(key) || [];
        existing.push(line);
        subClaseMap.set(key, existing);
      });

      const subClases: BudgetVsActualSubClase[] = [];

      subClaseMap.forEach((subClaseLines, subClaseName) => {
        const subClase2Map = new Map<string, BudgetVsActualLine[]>();

        subClaseLines.forEach((line) => {
          const key = line.sub_clase_2 || "Sin SubClase 2";
          const existing = subClase2Map.get(key) || [];
          existing.push(line);
          subClase2Map.set(key, existing);
        });

        const subClases2: BudgetVsActualSubClase2[] = [];

        subClase2Map.forEach((subClase2Lines, subClase2Name) => {
          const grupoMap = new Map<string, BudgetVsActualLine[]>();

          subClase2Lines.forEach((line) => {
            const key = line.grupo_cuenta_id || "Sin Grupo";
            const existing = grupoMap.get(key) || [];
            existing.push(line);
            grupoMap.set(key, existing);
          });

          const grupos: BudgetVsActualGrupo[] = [];

          grupoMap.forEach((grupoLines) => {
            const accounts: BudgetVsActualAccount[] = grupoLines.map(
              (line) => ({
                account_id: line.account_id,
                account_code: line.account_code,
                account_name: line.account_name,
                budgetMes: line.budget_mes,
                realMes: line.real_mes,
                budgetAcumulado: line.budget_acumulado,
                realAcumulado: line.real_acumulado,
              })
            );

            const grupoLine = grupoLines[0];
            grupos.push({
              grupo_cuenta_id: grupoLine.grupo_cuenta_id,
              grupo_cuenta_code: grupoLine.grupo_cuenta_code,
              grupo_cuenta_name: grupoLine.grupo_cuenta_name,
              orden_grupo_cuenta: grupoLine.orden_grupo_cuenta || 0,
              ...sumValues(accounts),
              accounts,
            });
          });

          grupos.sort((a, b) => a.orden_grupo_cuenta - b.orden_grupo_cuenta);

          const subClase2Line = subClase2Lines[0];
          subClases2.push({
            sub_clase_2: subClase2Name,
            orden_subclase_2: subClase2Line.orden_subclase_2 || 0,
            ...sumValues(grupos),
            grupos,
          });
        });

        subClases2.sort((a, b) => a.orden_subclase_2 - b.orden_subclase_2);

        const subClaseLine = subClaseLines[0];
        subClases.push({
          sub_clase: subClaseName,
          orden_subclase: subClaseLine.orden_subclase || 0,
          ...sumValues(subClases2),
          subClases2,
        });
      });

      subClases.sort((a, b) => a.orden_subclase - b.orden_subclase);

      const claseLine = claseLines[0];
      clases.push({
        clase: claseName,
        orden_clase: claseLine.orden_clase || 0,
        ...sumValues(subClases),
        subClases,
      });
    });

    clases.sort((a, b) => a.orden_clase - b.orden_clase);

    const findClase = (name: string): BudgetVsActualValues =>
      clases.find((c) => c.clase === name) || zeroValues();

    const ingresos = findClase(RESULTADOS_CLASES.ingresos);
    const costos = findClase(RESULTADOS_CLASES.costos);
    const gastos = findClase(RESULTADOS_CLASES.gastos);

    // costos/gastos are negative in the signed convention, so utilidad is a sum
    const utilidadBruta = addValues(ingresos, costos);
    const utilidadOperacional = addValues(utilidadBruta, gastos);

    return {
      clases,
      unmapped,
      totals: {
        ingresos: {
          budgetMes: ingresos.budgetMes,
          realMes: ingresos.realMes,
          budgetAcumulado: ingresos.budgetAcumulado,
          realAcumulado: ingresos.realAcumulado,
        },
        costos: {
          budgetMes: costos.budgetMes,
          realMes: costos.realMes,
          budgetAcumulado: costos.budgetAcumulado,
          realAcumulado: costos.realAcumulado,
        },
        gastos: {
          budgetMes: gastos.budgetMes,
          realMes: gastos.realMes,
          budgetAcumulado: gastos.budgetAcumulado,
          realAcumulado: gastos.realAcumulado,
        },
        utilidadBruta,
        utilidadOperacional,
      },
    };
  },
};

/**
 * Cumplimiento (%) = real / presupuesto * 100. Signs cancel (both series are
 * signed), so gastos at 80% means under-executed. Returns null when the
 * budget is 0 — rendered as "—", never Infinity/NaN.
 */
export function getCumplimiento(real: number, budget: number): number | null {
  if (budget === 0) return null;
  return (real / budget) * 100;
}
